/**
 * FAQPage JSON-LD from text_block / text slices with include_in_faq_schema enabled.
 * @see https://developers.google.com/search/docs/appearance/structured-data/faqpage
 */

import { sanitizeJsonLdStrings } from "~/utils/jsonld-sanitize";

const MIN_FAQ_PAIRS = 2;

const HEADING_TYPES = new Set([
  "heading1",
  "heading2",
  "heading3",
  "heading4",
  "heading5",
  "heading6",
]);

const FAQ_SCHEMA_SLICE_TYPES = new Set(["text_block", "text"]);

/** @param {unknown} v @returns {string} */
function trimStr(v) {
  if (v == null) return "";
  return String(v).trim();
}

/** @param {string} s @returns {string} */
function normalizeQuestionKey(s) {
  return trimStr(s).toLowerCase().replace(/\s+/g, " ");
}

/** @param {{ question: string, answer: string }[]} pairs @returns {{ question: string, answer: string }[]} */
function dedupeFaqPairs(pairs) {
  const seen = new Set();
  const out = [];

  for (const p of Array.isArray(pairs) ? pairs : []) {
    const key = normalizeQuestionKey(p?.question);
    if (!key || seen.has(key)) continue;
    const question = trimStr(p.question);
    const answer = trimStr(p.answer);
    if (!question || !answer) continue;
    seen.add(key);
    out.push({ question, answer });
  }

  return out;
}

/** @param {Array} blocks @returns {{ question: string, answer: string }[]} */
function pairsFromRichTextBlocks(blocks) {
  if (!Array.isArray(blocks) || !blocks.length) return [];

  const pairs = [];
  let currentQuestion = "";
  let answerParts = [];

  const flush = () => {
    const answer = answerParts.join(" ").replace(/\s+/g, " ").trim();
    if (currentQuestion && answer) {
      pairs.push({ question: currentQuestion, answer });
    }
    currentQuestion = "";
    answerParts = [];
  };

  for (const block of blocks) {
    if (!block || typeof block !== "object") continue;
    const text = trimStr(block.text);
    if (!text) continue;
    if (HEADING_TYPES.has(block.type || "")) {
      flush();
      currentQuestion = text;
    } else {
      answerParts.push(text);
    }
  }
  flush();
  return pairs;
}

/** @param {Object} slice @returns {{ question: string, answer: string }[]} */
function pairsFromTextBlockSlice(slice) {
  const primary = slice?.primary;
  if (!primary || typeof primary !== "object") return [];

  const blocks = [
    ...(Array.isArray(primary.sub_title) ? primary.sub_title : []),
    ...(Array.isArray(primary.paragraph) ? primary.paragraph : []),
  ];
  return pairsFromRichTextBlocks(blocks);
}

/** @param {Object} slice @returns {{ question: string, answer: string }[]} */
function pairsFromTextSlice(slice) {
  const primary = slice?.primary;
  if (!primary || typeof primary !== "object") return [];

  const blocks = Array.isArray(primary.text) ? primary.text : [];
  return pairsFromRichTextBlocks(blocks);
}

/** @param {Object} slice @returns {boolean} */
function isFaqSchemaSlice(slice) {
  const type = trimStr(slice?.type || slice?.slice_type).toLowerCase();
  if (!FAQ_SCHEMA_SLICE_TYPES.has(type)) return false;
  return slice?.primary?.include_in_faq_schema === true;
}

/** @param {Object} slice @returns {{ question: string, answer: string }[]} */
function pairsFromFaqSchemaSlice(slice) {
  const type = trimStr(slice?.type || slice?.slice_type).toLowerCase();
  if (type === "text_block") return pairsFromTextBlockSlice(slice);
  if (type === "text") return pairsFromTextSlice(slice);
  return [];
}

/**
 * @param {Array} slices
 * @returns {{ question: string, answer: string }[]}
 */
export function extractFaqPairsFromSlices(slices) {
  const combined = [];

  for (const slice of Array.isArray(slices) ? slices : []) {
    if (!slice || typeof slice !== "object") continue;
    if (!isFaqSchemaSlice(slice)) continue;
    combined.push(...pairsFromFaqSchemaSlice(slice));
  }

  return dedupeFaqPairs(combined);
}

/** @param {{ question: string, answer: string }[]} pairs @returns {Object|null} */
function buildFaqPageSchema(pairs) {
  const list = Array.isArray(pairs) ? pairs : [];
  if (list.length < MIN_FAQ_PAIRS) return null;

  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: list.map(({ question, answer }) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
}

/**
 * @param {{ question: string, answer: string }[]} pairs
 * @param {string} [pageKey]
 * @returns {Object|null}
 */
export function getFaqPageSchemaScript(pairs, pageKey = "faq") {
  const schema = buildFaqPageSchema(pairs);
  if (!schema) return null;

  return {
    hid: `jsonld-faq-${String(pageKey)
      .replace(/[^a-z0-9_-]/gi, "-")
      .toLowerCase()}`,
    type: "application/ld+json",
    innerHTML: JSON.stringify(sanitizeJsonLdStrings(schema)),
  };
}
